import {
  OFFER_ICON_VIEWBOX,
  offerIconShapes,
  type OfferIconShape,
} from "@shared/offerIcons";

/**
 * The CMS's renderer for the shared offer-icon catalogue (`POS-5`).
 *
 * The geometry lives in `@shared/offerIcons` and is defined exactly once; this
 * is the thirty lines that turn it into SVG for one surface. POS-3/POS-7 add
 * the television's own copy and the guest companion gets a third, all reading
 * the same `OFFER_ICON_GLYPHS`.
 *
 * Why the JSX is not shared with them: `src/shared/` is compiled by three
 * tsconfigs and only one has a `jsx` option — the API server image and the
 * standalone `@nenda/foyer-shared` package both compile it with plain `tsc`.
 * A React component there would either fail those builds or, if "fixed" by
 * adding `jsx`, make the shared package implicitly React-dependent for the
 * server too. See `docs/memory/global/pitfalls/shared-alias-build-lanes.md`.
 *
 * `currentColor` by default: the CMS token palette, the TV theme and the
 * companion's own ink all differ, and a colour baked in here would fight
 * exactly one of them.
 */

interface Props {
  /** A catalogue key, or any stored string — unknown ones fall back. */
  name: string | null | undefined;
  size?: number;
  color?: string;
  stroke?: number;
  /** Set when the glyph carries the meaning on its own (a picker tile). */
  title?: string;
}

export default function OfferIcon({
  name,
  size = 28,
  color = "currentColor",
  stroke = 1.7,
  title,
}: Props) {
  const line = {
    fill: "none",
    stroke: color,
    strokeWidth: stroke,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  const draw = (shape: OfferIconShape, i: number) => {
    switch (shape.kind) {
      case "rect":
        return (
          <rect
            key={i}
            x={shape.x}
            y={shape.y}
            width={shape.width}
            height={shape.height}
            rx={shape.rx}
            {...line}
          />
        );
      case "circle":
        return (
          <circle key={i} cx={shape.cx} cy={shape.cy} r={shape.r} {...line} />
        );
      case "dot":
        // The one FILLED primitive - see `OfferIconShape`.
        return (
          <circle
            key={i}
            cx={shape.cx}
            cy={shape.cy}
            r={shape.r}
            fill={color}
            stroke="none"
          />
        );
      default:
        return <path key={i} d={shape.d} {...line} />;
    }
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox={OFFER_ICON_VIEWBOX}
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : "true"}
      aria-label={title}
      style={{ display: "block" }}
    >
      {title && <title>{title}</title>}
      {offerIconShapes(name).map(draw)}
    </svg>
  );
}
